import { formatDate } from '@/utils';
import { Icon } from '@/components/Icons';
import { STATUS_MESSAGES } from '@/constants/labels';
import { TextContent } from '@/components/Text';

interface Props {
  /** The date the outage was posted. */
  datePosted?: string;
  /** The message to display in the banner. */
  message?: string;
  /** The title to display in the banner. */
  title?: string;
}

/**
 * OutageAlertBanner is a component that displays a banner with a message about a
 * scheduled maintenance or outage. It uses the STATUS_MESSAGES constants for
 * default title and message, but allows for custom values to be passed
 * in as props.
 */
export const OutageAlertBanner: React.FC<Props> = ({
  datePosted = formatDate(new Date(), 'fullDatetime'),
  message = STATUS_MESSAGES.OUTAGE_DESCRIPTION,
  title = STATUS_MESSAGES.OUTAGE_TITLE,
}) => {
  return (
    <div
      className="flex flex-col items-start gap-2 rounded-lg bg-accent-surface-red p-4"
      data-date-posted={datePosted}
      data-testid="outage-alert-banner"
      role="alert"
    >
      <div className="flex-start flex items-center gap-2">
        <Icon color="var(--color-accent-border-red)" name="WarningDiamond" weight="fill" />
        <TextContent color="border-red" level={2} label={title} type="headline" variant="label" />
      </div>
      <div>
        <TextContent
          color="neutral-gray-800"
          label={message}
          type="paragraph"
          variant="bodySmall"
        />
      </div>
    </div>
  );
};

OutageAlertBanner.displayName = 'OutageAlertBanner';

export default OutageAlertBanner;
